import { doc, getDoc, collection, query, where, getDocs, getFirestore } from 'firebase/firestore';
import firebaseApp from '../../firebase/credenciales';
import * as XLSX from 'xlsx';

const db = getFirestore(firebaseApp);

// --- FUNCIONES AUXILIARES ---

/**
 * Separa nombres y apellidos estrictamente según la estructura de pl_empleados.
 * - Nombres: Campo 'nombres'
 * - Apellidos: Campo 'apellidos' (el primero es Paterno, el resto es Materno)
 * @param {object} trab - Objeto de datos del trabajador.
 * @returns {{nombres: string, apellido_paterno: string, apellido_materno: string}}
 */
const separarNombresApellidos = (trab) => {
  // 1. Columna 7: Nombres (Campo 'nombres')
  let nombres = trab.nombres || ""; 
  
  let apellido_paterno = "";
  let apellido_materno = "";

  // 2. Columnas 5 y 6: Apellido Paterno y Materno (Campo 'apellidos')
  if (trab.apellidos) { 
    const partes = trab.apellidos.trim().split(/\s+/); 
    if (partes.length >= 2) {
      apellido_paterno = partes[0];
      apellido_materno = partes.slice(1).join(" "); // Une el resto como apellido materno (ej. de "Perez de Cuellar")
    } else if (partes.length === 1) {
      apellido_paterno = partes[0];
    }
  }

  return { nombres, apellido_paterno, apellido_materno };
};

/**
 * Mapea el nombre de la AFP a su código numérico para el nombre del archivo.
 */
const obtenerCodigoAFP = (nombre) => {
  const codigos = {
    'PRIMA': '23', 'INTEGRA': '02', 'PROFUTURO': '54', 'HABITAT': '35'
  };
  const nombreNormalizado = nombre.toUpperCase().trim();
  return codigos[nombreNormalizado] || 'XX';
};


// --- FUNCIÓN PRINCIPAL ---

/**
 * Genera archivo Excel para AFPNET con el formato oficial, sin cabeceras.
 * @param {string} planillaId - ID de la planilla a exportar
 */
export const generarAFPNet = async (planillaId) => {
  try {
    // 1. Obtener datos de Planilla y Periodo
    const planillaRef = doc(db, "pl_planillas", planillaId);
    const planillaDoc = await getDoc(planillaRef);
    if (!planillaDoc.exists()) throw new Error("Planilla no encontrada");
    const planilla = { id: planillaDoc.id, ...planillaDoc.data() };

    const periodoDevengue = planilla.periodo; // Formato: YYYY-MM
    const periodYear = parseInt(periodoDevengue.substring(0, 4), 10);
    const periodMonth = parseInt(periodoDevengue.substring(5, 7), 10);

    // 2. Obtener y Enriquecer datos de Detalle
    const detalleQuery = query(collection(db, "pl_planilla_detalle"), where("planilla_id", "==", planillaId));
    const detalleSnapshot = await getDocs(detalleQuery);
    const detalles = [];
    detalleSnapshot.forEach(doc => detalles.push({ id: doc.id, ...doc.data() }));

    const detallesEnriquecidos = await Promise.all(
      detalles.map(async (detalle) => {
        const dni = detalle.empleado_id || detalle.numero_documento;
        if (!dni) return detalle;

        const empleadoRef = doc(db, "pl_empleados", dni);
        const empleadoDoc = await getDoc(empleadoRef);
        if (empleadoDoc.exists()) {
          const empleadoData = empleadoDoc.data();
          return {
            ...empleadoData, 
            ...detalle, 
            descuentos: detalle.descuentos,
            total_ingresos_afectos: detalle.total_ingresos_afectos,
            cuspp: empleadoData.cuspp || detalle.cuspp,
            afp: empleadoData.afp || detalle.afp,
            nombres: empleadoData.nombres || detalle.nombres, // Sobrescribir con el maestro
            apellidos: empleadoData.apellidos || detalle.apellidos // Sobrescribir con el maestro
          };
        }
        return detalle;
      })
    );

    const trabajadoresAFP = detallesEnriquecidos.filter(detalle => {
        const nombreAFP = detalle.afp || detalle.descuentos?.sistema_pension?.nombre_afp;
        return detalle.regimen_pensionario !== 'ONP' && nombreAFP;
    });

    if (trabajadoresAFP.length === 0) throw new Error("No se encontraron empleados afiliados a AFP en la planilla.");

    // --- FUNCIONES DE COMPARACIÓN DE FECHAS (ASUMIENDO FIREBASE TIMESTAMP) ---

    const isDateInPeriod = (dateField) => {
        if (!dateField) return false;
        let date;
        if (dateField.seconds) {
            date = new Date(dateField.seconds * 1000);
        } else if (dateField instanceof Date) {
            date = dateField;
        } else {
            date = new Date(dateField);
        }
        
        if (isNaN(date.getTime())) return false;
        return date.getFullYear() === periodYear && (date.getMonth() + 1) === periodMonth;
    };
    
    const isDateBeforePeriodStart = (dateField) => {
        if (!dateField) return false;
        let date;
        if (dateField.seconds) {
            date = new Date(dateField.seconds * 1000);
        } else if (dateField instanceof Date) {
            date = dateField;
        } else {
            date = new Date(dateField);
        }

        if (isNaN(date.getTime())) return false;
        const dateYear = date.getFullYear();
        const dateMonth = date.getMonth() + 1;

        if (dateYear < periodYear) return true;
        if (dateYear === periodYear && dateMonth < periodMonth) return true;
        return false;
    };

    // 4. Definir la lista de data (SIN cabeceras)
    const dataCarga = [];
    let secuencia = 1;

    // 5. Mapear y llenar la data
    trabajadoresAFP.forEach(trab => {
        const { nombres, apellido_paterno, apellido_materno } = separarNombresApellidos(trab);
        
        const tipoDoc = trab.tipo_documento === "DNI" ? "0" : (trab.tipo_documento === "CE" ? "1" : ""); 
        let dni = String(trab.dni || trab.numero_documento || "");
        
        // PADDING: Asegurar que el DNI tenga 8 dígitos (especialmente los que empiezan con cero)
        if (trab.tipo_documento === "DNI" && dni.length > 0) {
            dni = dni.padStart(8, '0');
        }

        const remAsegurable = parseFloat((trab.total_ingresos_afectos || 0).toFixed(2));
        
        // CÁLCULO DE COLUMNAS DE SITUACIÓN LABORAL
        const ceseRL = isDateInPeriod(trab.fecha_cese) ? "S" : "N";
        const inicioRL = isDateInPeriod(trab.fecha_ingreso) ? "S" : "N";

        let relacionLaboral = "S"; 
        if (isDateBeforePeriodStart(trab.fecha_cese)) {
            relacionLaboral = "N";
        } else {
            relacionLaboral = "S";
        }
        
        // Columna 11: Excepción de Aportar (siempre vacía)
        const excepcionAportar = ''; 
        
        // Otros códigos de situación estándar:
        const tipoTrabajo = 'N'; 

        // Rellenar la fila de data (17 campos en total)
        // Se usa el formato {v: valor, t: 's'} para forzar a Excel a tratarlo como texto
        dataCarga.push([
            secuencia++,                                // 1. Número de secuencia
            { v: String(trab.cuspp || ""), t: 's' },   // 2. CUSPP (Texto)
            { v: tipoDoc, t: 's' },                    // 3. Tipo de documento (Texto)
            { v: dni, t: 's' },                        // 4. Número de documento (Texto)
            apellido_paterno,                           // 5. Apellido paterno
            apellido_materno,                           // 6. Apellido materno
            nombres,                                    // 7. Nombres
            relacionLaboral,                            // 8. Relación Laboral S/N
            inicioRL,                                   // 9. Inicio de RL S/N
            ceseRL,                                     // 10. Cese de RL S/N
            excepcionAportar,                           // 11. Excepción de Aportar
            remAsegurable,                              // 12. Remuneración asegurable
            0.00,                                       // 13. Aporte voluntario c/ fin previsional
            0.00,                                       // 14. Aporte voluntario s/ fin previsional
            0.00,                                       // 15. Aporte voluntario empleador
            tipoTrabajo,                                // 16. Tipo de trabajo o Rubro
            "",                                         // 17. AFP (Dejar en blanco)
        ]);
    });

    // 6. Crear el libro y la hoja de carga
    const wsCarga = XLSX.utils.aoa_to_sheet(dataCarga, { skipHeader: true }); // Omite la cabecera

    // Aplicar formato de TEXTO para CUSPP, Tipo Doc y Nro Doc
    wsCarga['!cols'] = [
        { wch: 15 }, 
        { wch: 15, z: "@" }, // CUSPP 
        { wch: 5, z: "@" }, // Tipo Doc.
        { wch: 15, z: "@" }, // Número Doc.
        { wch: 15 }, 
        { wch: 15 }, 
        { wch: 15 }, 
        { wch: 15 }, 
        { wch: 10 }, 
        { wch: 10 }, 
        { wch: 15 }, // Excepción de Aportar
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 20 },
        { wch: 10 }, // AFP
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, wsCarga, "CARGA_AFPNET"); 

    // 7. Generar el nombre de archivo y descargarlo
    const afpEjemplo = (trabajadoresAFP[0] && (trabajadoresAFP[0].afp || trabajadoresAFP[0].descuentos?.sistema_pension?.nombre_afp)) || 'AFP';
    const codigoAFP = obtenerCodigoAFP(afpEjemplo);
    const periodo = (planilla.periodo || "0000-00").replace("-", "");
    const nombreArchivo = `AFPNET_CARGA_${planilla.ruc_empresa}_${periodo}_${codigoAFP}.xlsx`;
    
    XLSX.writeFile(wb, nombreArchivo);

    return {
      success: true,
      message: `Archivo de carga AFPNET (Excel) generado correctamente con el formato oficial (sin cabeceras).`
    };

  } catch (error) {
    console.error("Error al generar AFPNET (Excel):", error);
    throw error;
  }
};